import { Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { cn } from '@/shared/utils/utils';
import { Badge } from '@/shared/components/ui/Badge';
import wordIcon from '../../assets/word.svg';
import googleDriveIcon from '../../assets/google-drive.svg';
import confluenceIcon from '../../assets/confluence.svg';
import notionIcon from '../../assets/notion.svg';

interface IntegrationSource {
  id: string;
  nameKey: string;
  descriptionKey: string;
  icon: string;
  alt: string;
  isActive: boolean;
}

const sources: IntegrationSource[] = [
  {
    id: 'word',
    nameKey: 'integrations.word.name',
    descriptionKey: 'integrations.word.description',
    icon: wordIcon,
    alt: 'Word',
    isActive: true,
  },
  {
    id: 'google-drive',
    nameKey: 'integrations.googleDrive.name',
    descriptionKey: 'integrations.googleDrive.description',
    icon: googleDriveIcon,
    alt: 'Google Drive',
    isActive: true,
  },
  {
    id: 'confluence',
    nameKey: 'integrations.confluence.name',
    descriptionKey: 'integrations.confluence.description',
    icon: confluenceIcon,
    alt: 'Confluence',
    isActive: true,
  },
  {
    id: 'notion',
    nameKey: 'integrations.notion.name',
    descriptionKey: 'integrations.notion.description',
    icon: notionIcon,
    alt: 'Notion',
    isActive: false,
  },
];

interface ImportSourceStepProps {
  onSourceSelected: (sourceId: string) => void;
}

export const ImportSourceStep = ({
  onSourceSelected,
}: ImportSourceStepProps) => {
  const { t } = useTranslation('knowledge');

  const handleClick = (source: IntegrationSource) => {
    if (source.isActive) {
      onSourceSelected(source.id);
    } else {
      toast.info(
        t('integrations.comingSoonMessage' as any)(t(source.nameKey as any))
      );
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 px-10 pt-4 overflow-y-auto">
        <div className="grid grid-cols-2 gap-4">
          {sources.map((source) => (
            <div
              key={source.id}
              className={cn(
                'group relative flex flex-col items-center gap-3 rounded-xl border p-6 transition-all duration-200',
                source.isActive
                  ? 'cursor-pointer border-slate-200 bg-white hover:border-primary/30 hover:bg-slate-50 hover:shadow-md'
                  : 'cursor-default border-slate-200 bg-slate-100 opacity-55'
              )}
              onClick={() => handleClick(source)}
            >
              {!source.isActive && (
                <Badge
                  variant="secondary"
                  className="absolute top-3 end-3 text-[10px] px-1.5 py-0 h-4 bg-gradient-to-r from-purple-100 to-blue-100 text-purple-700 border-0"
                >
                  <Sparkles className="h-2.5 w-2.5 me-0.5" />
                  {t('integrations.comingSoon')}
                </Badge>
              )}
              <div className="flex items-center justify-center w-12 h-12 bg-white rounded-lg">
                <img src={source.icon} alt={source.alt} className="w-10 h-10" />
              </div>
              <div className="text-center">
                <p
                  className={cn(
                    'font-medium text-sm',
                    source.isActive ? 'text-slate-900' : 'text-slate-500'
                  )}
                >
                  {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                  {t(source.nameKey as any)}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                  {t(source.descriptionKey as any)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
